import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import * as _ from 'lodash-es';
import { DecimalPipeWrapper } from '../../../../app/shared/pipes/decimal-pipe-wrapper/decimal-pipe-wrapper.pipe';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { CommonStandaloneModule } from 'src/app/shared/common-standalone.module';
import { UnitsAdapter } from '../../../../app/operational-data-common/services/units-adapter/units.adapter';
import { SizeType } from '@vi-iot/ui/models/size.model';
import { ItemComponentHelper } from './item.component.helper';
import { ViIconComponent } from '../icon/icon.component';
import { matTooltipSetter } from 'src/app/shared/functions/mat-tooltip-setter/mat-tooltip-setter';

@Component({
  selector: 'vi-item',
  standalone: true,
  imports: [
    CommonStandaloneModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    ViIconComponent,
  ],
  templateUrl: 'item.component.html',
  styleUrls: ['item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViItemComponent {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }

  currentOptionValue: any;
  private _value: any;
  @Input() stackValuesVertically = false;
  @Input() title: string;
  @Input() size = 'small';
  @Input() color = 'light';
  @Input() inversed = false;
  @Input() asDropdown = false;
  @Input() error: boolean;
  @Input() unit = '';
  @Input() options: ItemComponentHelper.ItemOption[] = [];
  @Input() mantissaPlaces: number;
  @Input() disableNumberFormatting: boolean;
  @Input() sizeOfDropdownBox: SizeType = 'large';
  @Input() valueSpecification: {
    min?: number;
    max?: number;
    stepping?: number;
  };

  constructor(public unitsAdapter: UnitsAdapter, private decimalPipeWrapper: DecimalPipeWrapper) {}

  @Input()
  set value(value: any | { value: any }) {
    // This fixes issue with change detection not detecting primitive value changes
    if (typeof value === 'object' && value !== null && value.hasOwnProperty('value')) {
      this._value = value.value;
    } else {
      this._value = value;
    }
    this.renderOptions();
  }
  get value(): any {
    if (this.mantissaPlaces && _.isEmpty(this.valueSpecification)) {
      // return only static, formatted value
      return this.addMantissa(this._value);
    } else if (this.stackValuesVertically) {
      return this._value.split(',');
    } else {
      return this._value;
    }
  }
  @Input() tooltipText: string;
  @Input() incorrectValue: number;

  @Output() valueChange: EventEmitter<any> = new EventEmitter<any>();

  isValue(): boolean {
    return !_.isUndefined(this.value) && !_.isNull(this.value);
  }

  renderOptions(): void {
    const spec = this.valueSpecification;
    const value: any = _.isString(this.value) || _.isNumber(this.value) ? this.value : '-';
    this.currentOptionValue = value;

    if (_.isEmpty(spec) || !_.isEmpty(this.options)) {
      return;
    }

    if (!_.isUndefined(spec.min) && !_.isUndefined(spec.max)) {
      let values = _.range(spec.min, spec.max + spec.stepping, spec.stepping);

      // Round the item values in case of step is float number
      values = values.map((v) => Math.round(v * 10) / 10);

      // Check if the last value is higher than max from valueSpecification
      // If is, remove it
      const lastValue = values[values.length - 1];
      if (lastValue > spec.max) {
        values.splice(-1, 1);
      }

      // Check if item values contain updated one. If don't, add it and sort values
      const t = values.find((v) => v.toString() === value.toString());
      if (_.isUndefined(t) && value !== '-') {
        values.push(value);
        values = _.sortBy(values);
      }

      if (value === '-') {
        (<any>values).unshift('-');
      }

      this.options = [];

      values.forEach((v: any) => {
        this.options.push({
          viewValue:
            v === '-'
              ? v
              : v !== null && v !== undefined
              ? (this.mantissaPlaces ? this.addMantissa(v) : v) + this.unit
              : '',
          value: v,
        });
      });

      this.options = this.options.map((option) => {
        return {
          value: option.value,
          viewValue: this.decimalPipeWrapper.transform(option.viewValue),
        };
      });
    }
  }

  onValueChange(value: any): void {
    this.currentOptionValue = value;
    this.valueChange.emit(value);
  }

  addMantissa(value: any): string {
    return parseFloat(value).toFixed(this.mantissaPlaces);
  }
}
